import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * Subscription Modal Component
 * Allows admin to manage user subscriptions
 */
const SubscriptionModal = ({ isOpen, onClose, onSuccess, userItem, setMessageModal }) => {
  const [startDate, setStartDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Populate form with existing subscription data
  useEffect(() => {
    if (userItem && isOpen) {
      // Format dates for input type="date" (YYYY-MM-DD)
      if (userItem.subscription_start) {
        const start = new Date(userItem.subscription_start);
        setStartDate(start.toISOString().split('T')[0]);
      } else {
        // Default to today
        setStartDate(new Date().toISOString().split('T')[0]);
      }
      
      if (userItem.subscription_expiry) {
        const expiry = new Date(userItem.subscription_expiry);
        setExpiryDate(expiry.toISOString().split('T')[0]);
      } else {
        // Default to 1 year from now
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        setExpiryDate(oneYearFromNow.toISOString().split('T')[0]);
      }
    }
  }, [userItem, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!startDate || !expiryDate) {
      setError('Veuillez remplir les deux dates');
      return;
    }
    
    const start = new Date(startDate);
    const expiry = new Date(expiryDate);
    
    if (expiry <= start) {
      setError('La date d\'expiration doit être après la date de début');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/admin/subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userItem.id,
          start_date: start.toISOString(),
          expiry_date: expiry.toISOString()
        })
      });
      
      if (response.ok) {
        setMessageModal({
          isOpen: true,
          title: 'Succès',
          message: 'Abonnement mis à jour avec succès!',
          type: 'success'
        });
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !userItem) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50" 
      style={{backgroundColor: 'rgba(0, 0, 0, 0.8)'}}
      data-testid="subscription-modal"
    >
      <div className="rounded-lg p-6 w-full max-w-md shadow-2xl" style={{backgroundColor: '#2C3148'}}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{color: '#FFFFFF'}}>Gérer l&apos;abonnement</h2>
          <button 
            onClick={onClose} 
            className="transition-colors duration-200"
            style={{color: '#8A92B2'}}
            onMouseEnter={(e) => e.currentTarget.style.color = '#FF5757'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#8A92B2'}
            data-testid="subscription-modal-close-btn"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 rounded" style={{backgroundColor: '#1C2133'}}>
          <p className="text-sm" style={{color: '#8A92B2'}}>
            <strong style={{color: '#FFFFFF'}}>Utilisateur:</strong> {userItem.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="subscription-form">
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Date de début
            </label>
            <input
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF',
                colorScheme: 'dark'
              }}
              data-testid="subscription-start-date"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Date d&apos;expiration
            </label>
            <input
              type="date"
              required
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF',
                colorScheme: 'dark'
              }}
              data-testid="subscription-expiry-date"
            />
          </div>

          {error && (
            <div 
              className="text-sm p-3 rounded-md" 
              style={{
                color: '#FF5757',
                backgroundColor: 'rgba(255, 87, 87, 0.1)',
                border: '1px solid #FF5757'
              }}
              data-testid="subscription-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md transition-colors duration-200"
              style={{
                color: '#8A92B2',
                borderColor: '#1C2133'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1C2133';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#8A92B2';
              }}
              data-testid="subscription-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 rounded-md transition-all duration-300 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
              data-testid="subscription-submit-btn"
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;
